import type { UIMessagePart, UIDataTypes, UITools, UIMessage } from 'ai';
import type { Subscription } from 'rxjs';
import type { AIInterviewAnswer } from '@jms/model';

// ─── Core Types ──────────────────────────────────────────────────────────────

/** Shorthand for the generic part type used in templates */
export type AnyPart = UIMessagePart<UIDataTypes, UITools>;

/** Agent identifier */
export const AGENT_ID = 'workbuddy-agent' as const;

// Rolling idle window for the agent "session": both the fullpage last-open chat
// and the bottom tab bar reset after this much inactivity, so returning the next
// day starts fresh instead of restoring/reopening stale (and uncached, expensive)
// conversations. Renewed on any agent interaction.
export const AGENT_SESSION_FRESHNESS_MS = 12 * 60 * 60 * 1000; // 12 hours

// localStorage key for the fullpage (/agent) last-open chat memory. Shared so the popup
// tab bar can keep it in step — selecting a tab updates it, New Chat clears it, closing a
// chat removes it — and fullscreen opens to the chat you last focused in either surface.
// Read/write logic lives in agent.fullpage.memory; both surfaces go through that helper.
export const AGENT_FULLPAGE_ACTIVE_THREAD_KEY = 'agent-fullpage-active-thread';

// ─── Model Tier ────────────────────────────────────────────────────────────────

// Coarse, model-name-free model selection surfaced in the composer. 'default' is the recommended
// everyday model; 'heavy' is slower and reserved for hard tasks. Only the tier string ever leaves
// the client — the backend maps it to a real model id and never reveals model names to the UI.
export type AgentModelTier = 'default' | 'heavy';

// Per-user persisted choice for the composer model selector.
export const AGENT_MODEL_TIER_KEY = 'agent-model-tier';

export const DEFAULT_AGENT_MODEL_TIER: AgentModelTier = 'default';

// Read the persisted composer tier. Falls back to the default when storage is unavailable
// (private mode / disabled) or unset, so callers always get a valid tier.
export function readPersistedModelTier(): AgentModelTier {

  try {
    return localStorage.getItem(AGENT_MODEL_TIER_KEY) === 'heavy' ? 'heavy' : DEFAULT_AGENT_MODEL_TIER;
  } catch {
    return DEFAULT_AGENT_MODEL_TIER;
  }
}

// Coerce an arbitrary thread-metadata value into a valid tier, or null when none is stored.
// Pre-feature threads (and any thread whose first message predates persistence) have no stored
// tier, so this returns null and callers fall back to the user's persisted default.
export function coerceThreadModelTier(value: unknown): AgentModelTier | null {

  if (value === 'heavy') return 'heavy';
  if (value === 'default') return 'default';
  return null;
}

// Persist the composer tier. A storage failure is non-fatal: the selection still applies in-memory
// for the session, it just won't survive a reload.
export function writePersistedModelTier(tier: AgentModelTier): void {

  try {
    localStorage.setItem(AGENT_MODEL_TIER_KEY, tier);
  } catch {
    // localStorage unavailable; in-memory selection still applies for this session.
  }
}

// ─── Thread ──────────────────────────────────────────────────────────────────

/** Thread from backend API */
export interface AgentThread {
  id: string;
  title?: string;
  resourceId: string;
  metadata?: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
}

/** A group of threads bucketed by relative date label */
export interface ThreadGroup {
  label: string;
  threads: AgentThread[];
}

/** A thread tab in the bottom bar */
export interface ThreadTab {
  threadId: string;
  title: string;
}

/** The bottom-bar tabs as saved in localStorage; `ts` is the last tab interaction, used for the session-freshness window. */
export interface SavedTabsState {
  tabs: ThreadTab[];
  activeTabId: string | null;
  ts: number;
}

export interface EnrichedThreadTab extends ThreadTab {
  streaming: boolean;
  unread: boolean;
}

// ─── Shortcut Hints ─────────────────────────────────────────────────────────

/** A single shortcut hint entry for the shared hints bar */
export interface ShortcutHint {
  text: string;
}

// ─── Card Actions (Generative UI) ───────────────────────────────────────────

/** Card action values for state persistence — shared across all generative UI cards */
export type CardAction = 'approved' | 'declined' | 'selected' | 'skipped';

// ─── InterviewQuestion Card (multi-question picker) ─────────────────────────────────

/** One option within a question */
export interface InterviewQuestionOption {
  label: string;
  description?: string;
}

/** A single question in a multi-question card */
export interface InterviewQuestion {
  question: string;
  mode: 'single' | 'multiple' | 'rank';
  options: InterviewQuestionOption[];
  allowFreetext?: boolean;
}

/** Data shape returned by the interview tool */
export interface InterviewCardData {
  questions: InterviewQuestion[];
  presented: boolean;
}

/** Extended metadata for question card persistence */
export interface InterviewCardActionMeta {
  action: CardAction;
  timestamp: number;
  answers: AIInterviewAnswer[];
}

// ─── File Upload ─────────────────────────────────────────────────────────────

/** Tracks a file from selection through upload to ready/error */
export interface PendingFile {
  file: File;
  id: string;
  blobUrl?: string;
  progress: number;
  status: 'uploading' | 'ready' | 'error';
  error?: string;
  subscription?: Subscription;
}

/** Progress state emitted during upload */
export interface UploadProgress {
  id: string;
  status: 'uploading' | 'ready' | 'error';
  progress: number;
  blobUrl?: string;
  error?: string;
}

// ─── Prompt Input ────────────────────────────────────────────────────────────

/** @mention item types */
export type MentionType = 'job' | 'customer' | 'contact';

/** A resolved @mention entry */
export interface MentionItem {
  id: string;
  type: MentionType;
  label: string;
  /** Color indicator for job mentions */
  color?: string;
}

/** Session context chip attached to a message */
export interface SessionContext {
  id: string;
  label: string;
}

// ─── Quick Actions (Welcome Screen) ─────────────────────────────────────────

/** Quick action button on the welcome screen */
export interface QuickAction {
  id: string;
  label: string;
  icon: string;
}

// ─── Suggest Actions (Role + Context aware) ─────────────────────────────────

/** Where the user currently is in the app, detected by AgentContextService */
export type SuggestContext = 'job' | 'workOrder' | 'default';

/** Normalised role bucket used to pick a suggestion set */
export type SuggestRole = 'administrator' | 'operations' | 'scheduler' | 'finance' | 'fallback';

/** Category tile id */
export type SuggestGroupId = 'operations' | 'scheduling' | 'customers' | 'finance';

/** A single category tile with its sub-prompts */
export interface SuggestGroup {
  id: SuggestGroupId;
  label: string;
  icon: string;
  prompts: string[];
}

/** Ordered tile set for a specific (role, context) cell */
export interface SuggestActionsConfig {
  groups: SuggestGroup[];
}

// ─── Activity Block Grouping ────────────────────────────────────────────────

/** A message part with its original index, used inside activity blocks */
export interface ActivityBlockPart {
  part: AnyPart;
  partIndex: number;
}

/** Discriminated union for items produced by groupMessageParts() */
export type RenderedItem =
  | { kind: 'text'; part: AnyPart; partIndex: number }
  | { kind: 'file'; part: AnyPart; partIndex: number }
  | { kind: 'special-tool'; part: AnyPart; partIndex: number }
  | { kind: 'activity-block'; parts: ActivityBlockPart[]; blockIndex: number };

// ─── File Constraints ────────────────────────────────────────────────────────

/** Shared file picker and drop limits for agent attachments. */
export const FILE_CONSTRAINTS = {
  ACCEPTED_TYPES: new Set([
    'image/jpeg', 'image/png', 'image/webp', 'image/heic',
    'application/pdf',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', 'text/csv', 'text/plain',
    // Email messages — browsers often report .eml/.msg as blank or application/octet-stream, so the
    // addFiles gate also falls back to ACCEPTED_EXTENSIONS for these (see chat.container.component).
    'message/rfc822',
  ]),
  ACCEPTED_EXTENSIONS: '.jpg,.jpeg,.png,.webp,.heic,.pdf,.xls,.xlsx,.csv,.txt,.docx,.eml,.msg',
  MAX_FILE_SIZE: 25 * 1024 * 1024,
  MAX_FILES_PER_MESSAGE: 10,
  MAX_MESSAGE_LENGTH: 64000,
} as const;

/** Retains the thread-load outcome during retry cooldown so unavailable threads stay blocked on revisit. */
export interface ThreadLoadFailure {
  failedAt: number;
  unavailable: boolean;
}
